/* ─────────────────────────────────────────────────────────────────────
   SI-17 · HE Custom Analytics Layer
   Fans HE-specific events out to GA4 (dataLayer + gtag), Meta Pixel
   (fbq trackCustom), and Klaviyo (_klOnsite). Shopify Pixel Manager
   already covers the standard e-commerce funnel (page_view, view_item,
   add_to_cart, begin_checkout, purchase) — this layer adds the
   HE-specific events the standard funnel cannot fire (terrain_entry,
   format switch, trophy view, provenance view, archive-ID verify,
   gift toggle, upsell impressions/clicks). Common props read from the
   #he-page-meta JSON island emitted by product-archive-main.liquid;
   absent on non-product pages (events that need it no-op gracefully).
   Append `?he_debug=1` to any URL to log every fired event to the
   console. NO Liquid edits beyond the JSON island + script tag — this
   file is isolated for easy removal.
   ───────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // ── Page meta from JSON island ──────────────────────────────────────
  var _pageMeta = null;
  function getPageMeta() {
    if (_pageMeta !== null) return _pageMeta;
    var el = document.getElementById('he-page-meta');
    if (!el) { _pageMeta = {}; return _pageMeta; }
    try { _pageMeta = JSON.parse((el.textContent || '').trim()) || {}; }
    catch (e) { _pageMeta = {}; }
    return _pageMeta;
  }

  // ── Helpers ─────────────────────────────────────────────────────────
  function getUTM(param) {
    var match = window.location.search.match(
      new RegExp('[?&]' + param + '=([^&]+)')
    );
    return match ? decodeURIComponent(match[1]) : '';
  }

  function getProductFormat() {
    var m = getPageMeta();
    if (m.product_format) return m.product_format;
    var bp = document.querySelector('[data-buy-panel]')
          || document.querySelector('[data-pdp-buy]')
          || document.querySelector('[data-product-format]');
    if (!bp) return '';
    return bp.getAttribute('data-format')
        || bp.getAttribute('data-product-format')
        || '';
  }

  function getCommonProps() {
    var m = getPageMeta();
    return {
      archive_id:     m.archive_id     || '',
      terrain_name:   m.terrain_name   || '',
      world:          m.world          || '',
      subworld:       m.subworld       || '',
      product_format: getProductFormat(),
      tier:           m.tier           || '',
      page_url:       window.location.href,
      traffic_source: getUTM('utm_source'),
      utm_campaign:   getUTM('utm_campaign'),
      utm_content:    getUTM('utm_content')
    };
  }

  function debug() {
    try {
      if (window.location.search.indexOf('he_debug=1') < 0) return;
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[HE Analytics]');
      console.log.apply(console, args);
    } catch (e) {}
  }

  // ── Core track() ────────────────────────────────────────────────────
  function track(eventName, props) {
    var payload = Object.assign({}, getCommonProps(), props || {});

    // GA4 via dataLayer (Shopify Pixel Manager pipes this to GA4)
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, he: payload });
    } catch (e) {}

    // GA4 direct (fallback if gtag is on-page)
    // addendum 1 §0.1 · Shopify's Google channel already sends the standard
    // `add_to_cart` to the same GA4 property, so sending ours under the same
    // name counted every add twice. Ours goes to gtag as `he_add_to_cart` —
    // it is the enriched one (via, archive_id, edition…) and is meant to sit
    // alongside the standard event, not replace or duplicate it. The
    // dataLayer push, Meta, Klaviyo and Clarity keep the original name.
    // Audited 22/09: of 23 HE events this is the only name that collides with
    // a GA4 recommended/reserved event.
    var GTAG_RENAME = { add_to_cart: 'he_add_to_cart' };
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', GTAG_RENAME[eventName] || eventName, payload);
      }
    } catch (e) {}

    // Meta Pixel custom event
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', eventName, payload);
      }
    } catch (e) {}

    // Klaviyo onsite queue
    try {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['track', eventName, payload]);
    } catch (e) {}

    // ── Microsoft Clarity · session tags (addendum 6 §3b) ─────────────
    // Four signals only, so a recording can be found for each of the
    // behaviours the events count: this is what turns "3% swiped" into
    // three sessions somebody can watch. Mirrored here rather than at each
    // call site, so add_to_cart — which is fired from theme.js — is covered
    // by the same rule. Guarded: an ad blocker leaves window.clarity
    // undefined, and analytics must never break the page.
    try {
      if (typeof window.clarity === 'function') {
        if (eventName === 'gallery_swipe')      window.clarity('set', 'he_swiped', 'true');
        else if (eventName === 'color_change')  window.clarity('set', 'he_changed_color', 'true');
        else if (eventName === 'select_format') window.clarity('set', 'he_edition_switch', String(payload.to_format || ''));
        else if (eventName === 'add_to_cart')   window.clarity('set', 'he_atc_via', String(payload.via || ''));
      }
    } catch (e) {}

    debug(eventName, payload);
  }

  // ── Expose globally ─────────────────────────────────────────────────
  window.HE = window.HE || {};
  window.HE.track = track;
  window.HE.getCommonProps = getCommonProps;
  window.HE.getPageMeta = getPageMeta;

  
  // ── DOM-ready event wiring ──────────────────────────────────────────
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }

  onReady(function () {
    var meta = getPageMeta();
    var isProductPage = /\/products\//.test(window.location.pathname);

    // ── P1.1 view_terrain_entry — fire once per product page load ──
    if (isProductPage && meta.page_type === 'product') {
      track('view_terrain_entry', {
        price: meta.current_price_cents != null
                 ? (meta.current_price_cents / 100)
                 : undefined,
        edition_type: meta.edition_type || ''
      });
    }

    // ── P1.2 select_format — click on .edition__seg <a> ──
    // The edition selector navigates to a new URL (each segment is an
    // <a href>), so we fire on click before navigation. The active
    // segment renders as a <button>; non-active as <a>. Only <a> fires.
    var editionTabs = document.querySelectorAll('.edition__seg');
    editionTabs.forEach(function (seg) {
      if (seg.tagName !== 'A') return;
      seg.addEventListener('click', function () {
        var label = (seg.textContent || '').trim().toLowerCase();
        var to_format = '';
        if (label.indexOf('tee') >= 0) to_format = 'tee';
        else if (label.indexOf('print') >= 0) to_format = 'print';
        else if (label.indexOf('complete') >= 0) to_format = 'complete';
        track('select_format', {
          from_format: meta.product_format || '',
          to_format:   to_format,
          price_from:  meta.current_price_cents != null
                         ? (meta.current_price_cents / 100)
                         : undefined
        });
      });
    });

    // ── P1.3 view_trophy — IntersectionObserver on the chip/stat block ──
    if ('IntersectionObserver' in window) {
      var trophyEls = document.querySelectorAll(
        '[data-trophy-chip], .pdp-stat, .trophy-stats'
      );
      if (trophyEls.length) {
        var trophySeen = new WeakSet();
        var trophyIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting || trophySeen.has(e.target)) return;
            trophySeen.add(e.target);
            var valueEl = e.target.querySelector(
              '.trophy-chip__value, .pdp-stat__value, .trophy-stats__value'
            );
            var labelEl = e.target.querySelector(
              '.trophy-chip__label, .pdp-stat__sub, .trophy-stats__label'
            );
            track('view_trophy', {
              trophy_value: meta.trophy_value
                || (valueEl ? (valueEl.textContent || '').trim() : ''),
              trophy_label: meta.trophy_label
                || (labelEl ? (labelEl.textContent || '').trim() : '')
            });
          });
        }, { threshold: 0.5 });
        trophyEls.forEach(function (el) { trophyIO.observe(el); });
      }
    }

    // ── P1.4 open_trophy_explainer — SKIPPED. No interactive toggle
    //    exists in the current template; trophy explainer copy renders
    //    inline as static text. Re-add this event when an expander is
    //    introduced.

    // ── P1.5 view_data_provenance — first time the table enters view ──
    if ('IntersectionObserver' in window) {
      var provEl = document.querySelector('[data-provenance-table]');
      if (provEl) {
        var provSeen = false;
        var provIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting && !provSeen) {
              provSeen = true;
              track('view_data_provenance', {});
            }
          });
        }, { threshold: 0.3 });
        provIO.observe(provEl);
      }
    }

    // ── P1.6 click_verify_archive_id — delegated click ──
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest
            ? e.target.closest('a[href^="/pages/data?id="], a[href*="/pages/data?id="]')
            : null;
      if (!a) return;
      var aid = '';
      try {
        var u = new URL(a.href, window.location.origin);
        aid = u.searchParams.get('id') || '';
      } catch (err) {}
      track('click_verify_archive_id', { archive_id: aid });
    });

    // ── P2.2 cart_gift_note_added — delegated change on the toggle ──
    // Fires when the gift toggle becomes checked (initial enable). The
    // existing initGiftPanel in theme.js handles the cart attribute
    // post; we only observe the state transition.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('[data-cart-gift-toggle]')) return;
      if (!t.checked) return;
      var section = t.closest('[data-cart-gift]');
      var msgEl = section && section.querySelector('[data-cart-gift-message]');
      var emailEl = section && section.querySelector('[data-cart-gift-email]');
      track('cart_gift_note_added', {
        has_message: !!(msgEl && (msgEl.value || '').trim().length),
        has_recipient_email: !!(emailEl && (emailEl.value || '').trim().length)
      });
    });

    // ── P2.3 cart_upsell_shown — IntersectionObserver on upsell card ──
    // Cart drawer is re-rendered after every /cart/add (section swap),
    // so we re-bind the observer whenever the drawer mutates. WeakSet
    // de-dupes; same node entering view twice fires only once.
    var upsellSeen = new WeakSet();
    var upsellIO = ('IntersectionObserver' in window)
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting || upsellSeen.has(e.target)) return;
            upsellSeen.add(e.target);
            var savingsEl = e.target.querySelector(
              '.cart-upsell-card__save-pill, .cart-upsell-card__save-text'
            );
            var savings = 0;
            if (savingsEl) {
              var m = (savingsEl.textContent || '').match(/\$([\d.]+)/);
              if (m) savings = parseFloat(m[1]);
            }
            track('cart_upsell_shown', {
              upsell_type: 'complete_edition',
              savings_amount: savings
            });
          });
        }, { threshold: 0.5 })
      : null;

    function wireUpsellCards(root) {
      if (!upsellIO) return;
      var cards = (root || document).querySelectorAll('.cart-upsell-card');
      cards.forEach(function (c) { upsellIO.observe(c); });
    }
    wireUpsellCards(document);

    // Watch the cart drawer for re-renders (Module E swaps innerHTML
    // after each /cart/add). New .cart-upsell-card nodes need to be
    // (re-)observed.
    var drawerNode = document.querySelector('[data-cart-drawer], #CartDrawer');
    if (drawerNode && typeof MutationObserver === 'function') {
      var drawerMO = new MutationObserver(function () {
        wireUpsellCards(drawerNode);
      });
      drawerMO.observe(drawerNode, { childList: true, subtree: true });
    }

    // ── P2.4 cart_upsell_clicked — delegated click on the CTA ──
    document.addEventListener('click', function (e) {
      var cta = e.target && e.target.closest
        ? e.target.closest('.cart-upsell-card__cta')
        : null;
      if (!cta) return;
      track('cart_upsell_clicked', { upsell_type: 'complete_edition' });
    });

    // ── P2.5 cart_upsell_dismissed — SKIPPED. Current upsell card has
    //    no dismiss control; add this when one is introduced.

    // ── P4.1 view_premiere_window — fires once per premiere PDP load ──
    // view_terrain_entry also fires (premiere is still a product page).
    // This event adds the premiere-specific funnel signals (cap/reserved/
    // status) that aren't relevant to archive PDPs.
    if (isProductPage && meta.tier === 'premiere') {
      track('view_premiere_window', {
        reserved_count:    meta.reserved_count != null ? meta.reserved_count : 0,
        max_edition_size:  meta.max_edition_size != null ? meta.max_edition_size : 0,
        window_state:      meta.window_state || '',
        display_state:     meta.display_state || '',
        status:            meta.display_state || meta.window_state || ''
      });
    }

    // ── P4.2 reserve_premiere_click — delegated click on premiere ATC ──
    // Premiere ATC button: <button class="atc pdp-buy__reserve" data-pdp-atc>
    // inside .pdp-buy--premiere. Click fires regardless of submit success;
    // the standard add_to_cart event (P2.1, fetch wrapper) covers the
    // successful resolution downstream. This event captures intent.
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest
        ? e.target.closest('.pdp-buy--premiere [data-pdp-atc], .pdp-buy--premiere .pdp-buy__reserve')
        : null;
      if (!btn) return;
      track('reserve_premiere_click', {
        variant_id:        btn.getAttribute('data-variant-id') || '',
        reserved_count:    meta.reserved_count != null ? meta.reserved_count : 0,
        max_edition_size:  meta.max_edition_size != null ? meta.max_edition_size : 0,
        window_state:      meta.window_state || '',
        display_state:     meta.display_state || ''
      });
    });

    /* ══════════════════════════════════════════════════════════════════
       2026-09-19 · addendum 4 §C · nine events.
       Same shape as gallery_lightbox_open: everything goes through track(),
       so it inherits the common props and fans out to dataLayer, gtag, Meta
       and Klaviyo, and each event carries `viewport`. All fire on a user
       action only — never on a programmatic state change, never on load.
       ══════════════════════════════════════════════════════════════════ */
    function viewport() {
      return (window.matchMedia && window.matchMedia('(max-width: 991px)').matches)
        ? 'mobile' : 'desktop';
    }

    // ── edition_switch · REMOVED 2026-09-19, addendum 6 §1 ────────────
    // `select_format` above already fires on exactly this action, with the
    // same tee|print|complete vocabulary plus price_from, and it has history
    // behind it. Two names for one action would double every total and split
    // the series at this build. select_format is the one that stays.
    // ──────────────────────────────────────────────────────────────────

    // ── 2 + 3 · gallery_swipe · gallery_reached_last ──────────────────
    // One event per settled slide: the scroll handler is debounced, so a
    // momentum swipe across three slides reports where it lands, not every
    // slide it passes. Dot taps run through the same path with direction
    // derived from the index change, so the two routes are comparable.
    // reached_last fires once per page view, not per visit to the slide.
    var rail = document.querySelector('.pdp-gallery__main');
    if (rail) {
      var slideCount = rail.querySelectorAll('.pdp-gallery__media').length;
      var lastIndex = 0;
      var reachedLastFired = false;
      var settleTimer = null;
      var pendingVia = 'swipe';

      var reportSlide = function (to, via) {
        if (to === lastIndex || to < 0 || to >= slideCount) return;
        track('gallery_swipe', {
          direction: to > lastIndex ? 'left' : 'right',
          from_index: lastIndex + 1,
          to_index: to + 1,
          slide_count: slideCount,
          viewport: viewport()
        });
        lastIndex = to;
        if (to > maxIndex) maxIndex = to;
        if (!reachedLastFired && to === slideCount - 1) {
          reachedLastFired = true;
          track('gallery_reached_last', {
            slide_count: slideCount,
            reached_via: via,
            viewport: viewport()
          });
        }
      };

      // addendum 6 §3 · depth, not just completion. reached_last is binary and
      // will read zero most of the time; this reports where people stop.
      // Fired once per page view on visibilitychange → hidden (not unload,
      // which is unreliable on mobile Safari). A gallery that was never
      // touched reports max_index 1 — "did not move" is the finding we most
      // need, and dropping it would bias the median upward.
      var maxIndex = 0;
      var depthFired = false;
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'hidden' || depthFired) return;
        depthFired = true;
        track('gallery_depth', {
          max_index: maxIndex + 1,
          slide_count: slideCount,
          viewed_ratio: slideCount ? +(((maxIndex + 1) / slideCount).toFixed(2)) : 0,
          viewport: viewport()
        });
      });

      rail.addEventListener('scroll', function () {
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          var idx = rail.offsetWidth ? Math.round(rail.scrollLeft / rail.offsetWidth) : 0;
          reportSlide(idx, pendingVia);
          pendingVia = 'swipe';
        }, 180);
      }, { passive: true });

      // the dots are built at runtime by he-mobile-scripts.js — delegate
      document.addEventListener('click', function (e) {
        var dot = e.target && e.target.closest ? e.target.closest('.pdp-gallery__dots span') : null;
        if (!dot) return;
        pendingVia = 'dot';
        var idx = parseInt(dot.dataset.idx, 10);
        if (!isNaN(idx)) setTimeout(function () { reportSlide(idx, 'dot'); }, 260);
      });
    }

    // ── 4 · color_change ──────────────────────────────────────────────
    var swatches = document.querySelectorAll('[data-pdp-swatch]');
    if (swatches.length) {
      var currentColor = (function () {
        var on = document.querySelector('[data-pdp-swatch][aria-pressed="true"]');
        return on ? on.getAttribute('data-option-value') : '';
      })();
      Array.prototype.forEach.call(swatches, function (sw, i) {
        sw.addEventListener('click', function () {
          var to = sw.getAttribute('data-option-value') || '';
          if (to === currentColor) return;   // re-tapping the active swatch is not a change
          track('color_change', {
            // addendum 7 · from_color / to_color rather than bare from / to:
            // the bare names read as generic and collide conceptually with
            // from_format / to_format on select_format.
            from_color: currentColor,
            to_color: to,
            position: i + 1,
            swatch_count: swatches.length,
            viewport: viewport()
          });
          currentColor = to;
        });
      });
    }

    // ── 5 · more_terrains_click ───────────────────────────────────────
    var moreLink = document.querySelector('.pdp-breadcrumb__more');
    if (moreLink) {
      moreLink.addEventListener('click', function () {
        track('more_terrains_click', { viewport: viewport() });
      });
    }

    // ── 6, 7, 8 · hamburger menu ──────────────────────────────────────
    // Bound to the DOM signals theme.js already toggles, so the menu module
    // is untouched. A dismissal only counts when no link was taken: tapping
    // a link is nav_menu_click, and double-counting it would make the
    // dismissal rate meaningless.
    var menuToggle = document.querySelector('[data-mobile-menu-toggle], .nav__hamburger');
    var menuEl = document.querySelector('[data-mobile-menu], .mobile-menu');
    if (menuToggle && menuEl) {
      var openedAt = 0;
      var menuIsOpen = false;

      var menuDismissed = function (method) {
        if (!menuIsOpen) return;
        menuIsOpen = false;
        track('nav_menu_dismiss', {
          method: method,
          dwell_ms: openedAt ? (Date.now() - openedAt) : 0,
          viewport: viewport()
        });
      };

      menuToggle.addEventListener('click', function () {
        // read after theme.js has flipped the attribute
        setTimeout(function () {
          var open = menuToggle.getAttribute('aria-expanded') === 'true';
          if (open && !menuIsOpen) {
            openedAt = Date.now();
            menuIsOpen = true;
            track('nav_menu_open', { viewport: viewport() });
          } else if (!open && menuIsOpen) {
            menuDismissed('close-button');
          }
        }, 0);
      });

      menuEl.addEventListener('click', function (e) {
        var link = e.target && e.target.closest ? e.target.closest('a[href]') : null;
        if (link) {
          var links = Array.prototype.slice.call(menuEl.querySelectorAll('a[href]'));
          track('nav_menu_click', {
            label: (link.textContent || '').trim(),
            href: link.getAttribute('href') || '',
            position: links.indexOf(link) + 1,
            viewport: viewport()
          });
          menuIsOpen = false;   // navigating away, not a dismissal
          return;
        }
        if (e.target && e.target.closest && e.target.closest('[data-mobile-menu-close]')) {
          menuDismissed('close-button');
        } else if (e.target === menuEl) {
          menuDismissed('backdrop');
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') menuDismissed('escape');
      });
      window.addEventListener('popstate', function () { menuDismissed('back'); });
    }

    debug('initialized', { meta: meta, isProductPage: isProductPage });
  });
})();
